<script lang="ts">
  import { Chip, ChipDeprecated, type FacetValue } from "@otto-ec/heureka-components/facet-value";
  import { ChipAction, type NextBestFilter } from "@otto-ec/heureka-components/filter";
  import { type FacetValueLabelOptions, Text } from "@otto-ec/heureka-components/facet-value-label";
  import DeselectAll from "../facetvaluetags/DeselectAll.svelte";
  import type { DeselectAllConfiguration } from "./type";
  import { OverflowBox } from "@otto-ec/heureka-components/facet-value-group";
  import { removeStatus } from "@otto-ec/heureka-components/tracking";
  import { heureka } from "@otto-ec/heureka";
  import { sanFilterWhatIf, sanPopularFacetValues } from "../stores/trackingStore";
  import { dereference, readLabel, updateLabel, updateStatus } from "../tracking/FeatureTracking";
  import { setChangeLabels } from "../filter/FilterTracking";
  import { emptyCategoryPathStore } from "../categorypath/categoryPathStore";
  import { eventQBus } from "../types/EventQBus";
  import { isActive, isNotActive } from "../toggle/Toggle";
  import { getActiveVariation } from "../experiment/Experiments";
  import { FilterSection } from "../filter/FilterSection";
  import { forEachElement } from "../util/Utils";
  import { clearFormInput, interceptSubmit } from "../filter/FilterFormActions";

  const defaultFacetValueLabelOptions: Partial<FacetValueLabelOptions> = {
    marginLeft: false,
    size: "75",
    showCount: false,
  };

  interface Props {
    nbaTitles?: string[];
    deselectAll: DeselectAllConfiguration;
    values?: FacetValue[];
    nextBestFilters?: NextBestFilter[];
    selectedValuesCount?: number;
  }

  const { nbaTitles, deselectAll, values = [], nextBestFilters = [], selectedValuesCount = 0 }: Props = $props();
  /*                                              */
  const uniqueNbaTitles = $derived(nbaTitles ? Array.from(new Set(nbaTitles)) : undefined);
  const topQuickFacet = $derived(uniqueNbaTitles?.join("|") || "none");
  /*                                                                             */
  const cleanedValues: FacetValue[] = $derived(values?.map(removeStatus) ?? []);
  const selectedValues: FacetValue[] = $derived(cleanedValues.filter((value) => value.checked) ?? []);
  const hasSelectedValues = $derived(selectedValues?.length > 0);
  const unselectedValues: FacetValue[] = $derived(cleanedValues.filter((value) => !value.checked) ?? []);
  const hasUnselectedValues = $derived(unselectedValues?.length > 0);

  const hasNextBestFilters = $derived(nextBestFilters.length > 0);
  const isNextBestFilterSelected = $derived(nextBestFilters.some((nbf) => nbf.selected));
  const isSelected = $derived(isNextBestFilterSelected || selectedValuesCount > 0);

  /*                                                                           */
  /*        */
  /*                                                                           */

  function onchange(event: Event) {
    if (event.target instanceof HTMLElement) {
      const referenced = dereference(event.target);
      const filterForm = referenced.closest("form");
      /*                                                                                           */
      /*                                                   */
      if (filterForm) {
        setChangeLabels(filterForm, { san_FacetLocation: "list_initial" });
        if (event.target instanceof HTMLInputElement && event.target.checked === false) {
          filterForm.dataset.tsFeatureFilterMethod = "x_single_top";
        } else {
          filterForm.dataset.tsFeatureFilterMethod = readLabel(referenced, "san_FilterMethod");
        }
      }
    }

    heureka.filterSection_change.emit({ target: event.target });
  }

  function onClickDeselectAll() {
    forEachElement<HTMLElement>(
      ".js_hc_tValue[data-checked='true'][data-ts-feature-status]",
      (elem) => {
        updateLabel(elem, "san_FilterMethod", "x_all_top");
        updateLabel(elem, "san_FacetLocation", "list_initial");
        updateStatus(elem, "deleted", 2);
      },
      FilterSection.template().filterSection,
    );
  }

  function onSubmitDeselectAll(event: SubmitEvent) {
    emptyCategoryPathStore();
    forEachElement<HTMLInputElement>(
      ".js_hc_tValue[data-checked='true'][data-ts-feature-status]",
      (elem) => {
        clearFormInput(elem);
      },
      FilterSection.template().filterSection,
    );
    interceptSubmit(event);
  }

  function openNextBestFilter(filterId: string) {
    eventQBus.emit("ftfind.filterSheet.openNextBestFilterDetail", { filterId: filterId });
  }

  /*                                                                                        */
  /*                                                                          */
  let nbaElement: HTMLDivElement | undefined = $state();
  let isOcMounted = false;
  $effect(() => {
    if (isNotActive("HEUREKA_1975_OC_CHIP_MIGRATION")) return;
    if (!nbaElement) return;

    const handler = (event: Event) => {
      if (isOcMounted) return;
      /*                                           */
      if (!(event.target instanceof HTMLElement) || !event.target.tagName.toLowerCase().startsWith("oc-chip")) return;
      isOcMounted = true;
      heureka.nextBestAction_loaded.emit();
    };

    nbaElement.addEventListener("oc-mount", handler);
    return () => nbaElement?.removeEventListener("oc-mount", handler);
  });

  $effect(() => {
    if (getActiveVariation("e3911", "StatusQuo") === "Filterbar" && isNotActive("HEUREKA_1975_OC_CHIP_MIGRATION")) {
      heureka.nextBestAction_loaded.emit();
    }
  });
</script>

{#snippet closeIcon()}
  <oc-icon-v1 class="hc_facetValue__icon" type="close" size="50"></oc-icon-v1>
{/snippet}

{#snippet chip(facetValue: FacetValue)}
  {#if isActive("HEUREKA_1975_OC_CHIP_MIGRATION")}
    <Chip class={[{ js_clearInput: facetValue.checked }]} {...facetValue} showRemoveIcon={!!facetValue.checked}>
      <Text {...facetValue} {...defaultFacetValueLabelOptions} />
    </Chip>
  {:else}
    <ChipDeprecated class={[{ js_clearInput: facetValue.checked }]} {...facetValue}>
      <Text {...facetValue} {...defaultFacetValueLabelOptions} />
      {#if facetValue.checked}
        {@render closeIcon()}
      {/if}
    </ChipDeprecated>
  {/if}
{/snippet}

{#snippet nextBestFilterChips(nextBestFilters: NextBestFilter[])}
  <div class="hc_filterGroup__container">
    <ul class="hc_filterGroup__list">
      {#each nextBestFilters as nextBestFilter (nextBestFilter.title)}
        <li class="hc_filterGroup__item">
          <ChipAction
            ref="find_filter_{nextBestFilter.id}"
            title={nextBestFilter.title}
            selected={nextBestFilter.selected}
            itemCount={nextBestFilter.itemCount}
            onclick={() => openNextBestFilter(nextBestFilter.id)}
          ></ChipAction>
        </li>
      {/each}
    </ul>
  </div>
{/snippet}

<div
  bind:this={nbaElement}
  class="hc_nextBestAction{isSelected ? '' : ' hc_nextBestAction--unselected'}"
  data-ts-top-quick-facet={topQuickFacet}
  {onchange}
>
  <div class="hc_nextBestAction__content">
    <div class="hc_nextBestAction__values">
      <div class="hc_nextBestAction__mini-button">
        <oc-icon-button-v3
          id="find_filterSheetButton"
          class="js_openFilterSheet find_filterSheetButton--mini-oc"
          data-ts-labels={JSON.stringify({
            san_Foldout: "filter_top_mini",
            san_PopularFacetValues: $sanPopularFacetValues,
            san_TopQuickFacet: topQuickFacet,
            san_FilterWhatIf: $sanFilterWhatIf,
          })}
          size="50"
          oc-aria-label="Filter"
          variant="transparent"
          icon="filter"
          icon-color="white"
          {...isActive("HEUREKA_1913_MIGRATE_TO_OC_SHEET")
            ? { "data-oc-sheet-v1-open": JSON.stringify({ id: "heureka-filtersheet" }) }
            : {}}
        >
        </oc-icon-button-v3>
        {#if selectedValuesCount > 0}
          <oc-badge-v2 class="hc_nextBestAction__mini-button-badge" size="100" variant="info"
            >{selectedValuesCount}</oc-badge-v2
          >
        {/if}
      </div>
      {#if hasSelectedValues}
        <OverflowBox values={selectedValues} item={chip}></OverflowBox>
      {/if}
      {#if hasNextBestFilters}
        {#if nextBestFilters.length > 0}
          {@render nextBestFilterChips(nextBestFilters)}
        {/if}
      {/if}
      {#if hasUnselectedValues}
        <OverflowBox values={unselectedValues} item={chip}></OverflowBox>
      {/if}
      <oc-button-v1
        id="find_filterSheetButton--secondary"
        class="js_openFilterSheet"
        data-ts-labels={JSON.stringify({
          san_Foldout: "filter_top_all",
          san_PopularFacetValues: $sanPopularFacetValues,
          san_TopQuickFacet: topQuickFacet,
          san_FilterWhatIf: $sanFilterWhatIf,
        })}
        size="50"
        oc-aria-label="Filter"
        variant="secondary"
        icon-type-right="filter"
        {...isActive("HEUREKA_1913_MIGRATE_TO_OC_SHEET")
          ? { "data-oc-sheet-v1-open": JSON.stringify({ id: "heureka-filtersheet" }) }
          : {}}
      >
        Alle Filter
      </oc-button-v1>
    </div>
    {#if isSelected}
      <div class="hc_nextBestAction__deselectAll">
        <DeselectAll {...deselectAll} onclick={onClickDeselectAll} onsubmit={onSubmitDeselectAll} />
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .hc_nextBestAction {
    padding-top: tokens.$oc-semantic-spacing-50;
    margin-bottom: tokens.$oc-semantic-spacing-50;

    &--unselected {
      padding-bottom: tokens.$oc-semantic-spacing-50;
    }

    ul {
      display: flex;
      gap: tokens.$oc-semantic-spacing-50;
      align-items: center;
    }

    &__content {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 14px 0;
    }

    &__mini-button {
      display: flex;
      &-badge {
        margin-left: calc(-1 * var(--oc-semantic-spacing-75));
      }
    }

    &__values {
      /*              */
      gap: tokens.$oc-semantic-spacing-50;
      flex: 1 1 auto;
      display: flex;
    }

    &__values {
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;

      .hc_facetValue__icon {
        width: tokens.$oc-semantic-spacing-75;
        height: tokens.$oc-semantic-spacing-75;
        margin-left: tokens.$oc-semantic-spacing-25;
      }
    }

    &__deselectAll {
      flex: 1;
      white-space: nowrap;
      text-align: right;
      padding-left: tokens.$oc-semantic-spacing-75;
      padding-right: tokens.$oc-semantic-spacing-25;
    }

    .find_filterSheetButton--mini-oc {
      padding: 0 tokens.$oc-semantic-spacing-50;
      flex: none;
      margin: 0;
      background: tokens.$oc-semantic-color-primary-background;
      border-radius: tokens.$oc-semantic-border-radius-100;
    }
  }
</style>
